import { IS_TAURI } from './platformUtils';
import { open, save as tauriSave } from '@tauri-apps/plugin-dialog';
import { readTextFile, writeFile, writeTextFile } from '@tauri-apps/plugin-fs';

// Check if File System Access API is supported
const supportsFileSystemAccess =
  typeof window !== 'undefined' &&
  'showSaveFilePicker' in window &&
  'showOpenFilePicker' in window;

export interface FileHandle {
  path?: string; // Tauri file path
  handle?: FileSystemFileHandle; // Browser file handle
}

export interface FileFilter {
  name: string;
  extensions: string[];
}

export interface FileTypeDescriptor {
  description: string;
  accept: Record<string, string[]>;
}

export type FileContent = string | Uint8Array;

export interface OpenFileOptions {
  filters?: FileFilter[];
  multiple?: boolean;
}

export interface SaveFileOptions {
  defaultFilename?: string;
  filters?: FileFilter[];
}

/**
 * Convert filters to browser File System Access API format
 */
function filtersToBrowserTypes(filters: FileFilter[]): FileTypeDescriptor[] {
  return filters.map(filter => ({
    description: filter.name,
    accept: {
      [`application/${filter.extensions[0] || 'octet-stream'}`]:
        filter.extensions.map(ext => `.${ext}`),
    },
  }));
}

/**
 * Open a file and return its contents as text or binary
 */
export async function openFileGeneric(
  options: OpenFileOptions = {},
): Promise<{ content: FileContent; fileHandle: FileHandle } | null> {
  const { filters, multiple = false } = options;

  try {
    if (IS_TAURI) {
      // Tauri: Use native file dialog
      const filePath = await open({
        multiple,
        filters: filters || [{ name: 'All Files', extensions: ['*'] }],
      });

      if (!filePath || typeof filePath !== 'string') {
        return null;
      }

      const content = await readTextFile(filePath);

      return {
        content,
        fileHandle: { path: filePath },
      };
    } else if (supportsFileSystemAccess) {
      // Modern browser: Use File System Access API
      const pickerOpts: any = {
        multiple,
      };

      if (filters && filters.length > 0) {
        pickerOpts.types = filtersToBrowserTypes(filters);
      }

      const [handle] = await (window as any).showOpenFilePicker(pickerOpts);

      if (!handle) {
        return null;
      }

      const file = await handle.getFile();
      if (!file) {
        return null;
      }

      const content = await file.text();

      return {
        content,
        fileHandle: { handle },
      };
    } else {
      // Fallback: Use traditional file input
      return new Promise(resolve => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';

        if (filters && filters.length > 0) {
          const extensions = filters.flatMap(f =>
            f.extensions.map(ext => `.${ext}`),
          );
          fileInput.accept = extensions.join(',');
        }

        fileInput.onchange = async e => {
          const target = e.target as HTMLInputElement;
          if (target.files && target.files.length > 0) {
            const file = target.files[0];
            if (!file) {
              resolve(null);
              return;
            }
            const content = await file.text();
            resolve({ content, fileHandle: {} });
          } else {
            resolve(null);
          }
        };

        fileInput.click();
      });
    }
  } catch (error) {
    console.error('Failed to open file:', error);
    return null;
  }
}

/**
 * Open a JSON file and return its parsed contents
 */
export async function openFileJSON(): Promise<{
  data: any;
  fileHandle: FileHandle;
} | null> {
  const result = await openFileGeneric({
    filters: [{ name: 'JSON', extensions: ['json'] }],
  });

  if (!result) {
    return null;
  }

  try {
    const data = JSON.parse(result.content as string);
    return {
      data,
      fileHandle: result.fileHandle,
    };
  } catch (error) {
    console.error('Failed to parse JSON:', error);
    return null;
  }
}

/**
 * Save content to a file. If fileHandle is provided and valid, saves to that file.
 * Otherwise prompts for a new file location.
 */
export async function saveFileGeneric(
  content: FileContent,
  fileHandle?: FileHandle,
  options: SaveFileOptions = {},
): Promise<FileHandle | null> {
  const {
    defaultFilename = 'file.txt',
    filters = [{ name: 'All Files', extensions: ['*'] }],
  } = options;

  try {
    if (IS_TAURI) {
      // Tauri: Try to use existing path or show save dialog
      let filePath = fileHandle?.path;

      if (!filePath) {
        const newPath = await tauriSave({
          defaultPath: defaultFilename,
          filters,
        });

        if (!newPath) {
          return null;
        }

        filePath = newPath;
      }

      if (typeof content === 'string') {
        await writeTextFile(filePath, content);
      } else {
        await writeFile(filePath, content);
      }

      return { path: filePath };
    } else if (supportsFileSystemAccess) {
      // Modern browser: Try to use existing handle or get new one
      let handle = fileHandle?.handle;

      if (!handle) {
        handle = await (window as any).showSaveFilePicker({
          suggestedName: defaultFilename,
          types: filtersToBrowserTypes(filters),
        });
      }

      if (!handle) {
        return null;
      }

      // Request permission if needed (using any to access non-standard API)
      const permission = await (handle as any).queryPermission?.({
        mode: 'readwrite',
      });
      if (permission && permission !== 'granted') {
        const newPermission = await (handle as any).requestPermission?.({
          mode: 'readwrite',
        });
        if (newPermission !== 'granted') {
          throw new Error('Permission denied');
        }
      }

      const writable = await handle.createWritable();
      await writable.write(content as any); // FileContent is compatible but TypeScript doesn't recognize it
      await writable.close();

      return { handle };
    } else {
      // Fallback: Download file
      fallbackDownload(content, defaultFilename);
      return {};
    }
  } catch (error) {
    console.error('Failed to save file:', error);
    return null;
  }
}

/**
 * Save data as JSON to a file. If fileHandle is provided and valid, saves to that file.
 * Otherwise prompts for a new file location.
 */
export async function saveFileJSON(
  data: any,
  fileHandle?: FileHandle,
  defaultFilename?: string,
): Promise<FileHandle | null> {
  const content = JSON.stringify(data, null, 2);
  return saveFileGeneric(content, fileHandle, {
    defaultFilename: defaultFilename || 'logbook.json',
    filters: [{ name: 'JSON', extensions: ['json'] }],
  });
}

/**
 * Save data to a new file (always prompts for location)
 */
export async function saveFileAs(
  data: any,
  defaultFilename?: string,
): Promise<FileHandle | null> {
  return saveFileJSON(data, undefined, defaultFilename);
}

/**
 * Download file content (text or binary) with given filename.
 *
 * If on browser, uses fallbackDownload function. If on Tauri, uses Tauri APIs.
 */
export async function downloadFile(
  content: FileContent,
  filename: string,
): Promise<void> {
  if (IS_TAURI) {
    // Tauri: Use native save dialog
    const filePath = await tauriSave({
      defaultPath: filename,
      filters: [{ name: 'All Files', extensions: ['*'] }],
    });

    if (!filePath) {
      return;
    }

    if (typeof content === 'string') {
      await writeTextFile(filePath, content);
    } else {
      await writeFile(filePath, content);
    }
  } else {
    // Browser: Use fallback download method
    fallbackDownload(content, filename);
  }
}

/**
 * Fallback method to download a file
 */
function fallbackDownload(content: FileContent, filename: string) {
  let dataUrl: string;

  if (typeof content === 'string') {
    // Text content
    const mimeType = getMimeType(filename);
    dataUrl = `data:${mimeType};charset=utf-8,` + encodeURIComponent(content);
  } else {
    // Binary content (Uint8Array)
    const blob = new Blob([content as any], { type: getMimeType(filename) });
    dataUrl = URL.createObjectURL(blob);
  }

  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute('href', dataUrl);
  downloadAnchorNode.setAttribute('download', filename);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();

  // Clean up blob URL if it was created
  if (typeof content !== 'string') {
    setTimeout(() => URL.revokeObjectURL(dataUrl), 100);
  }
}

/**
 * Get MIME type based on file extension
 */
function getMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();

  const mimeTypes: Record<string, string> = {
    json: 'application/json',
    csv: 'text/csv',
    txt: 'text/plain',
    html: 'text/html',
    xml: 'application/xml',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    svg: 'image/svg+xml',
  };

  return mimeTypes[ext || ''] || 'application/octet-stream';
}

// ============================================================================
// Higher-level convenience functions for specific formats
// ============================================================================

/**
 * Save data as CSV
 */
export async function saveAsCSV(
  csvContent: string,
  fileHandle?: FileHandle,
  defaultFilename: string = 'export.csv',
): Promise<FileHandle | null> {
  return saveFileGeneric(csvContent, fileHandle, {
    defaultFilename,
    filters: [{ name: 'CSV', extensions: ['csv'] }],
  });
}

/**
 * Save binary data (e.g., Excel files)
 */
export async function saveAsBinary(
  binaryContent: Uint8Array,
  fileHandle?: FileHandle,
  defaultFilename: string = 'export.xlsx',
  filters: FileFilter[] = [{ name: 'Excel', extensions: ['xlsx'] }],
): Promise<FileHandle | null> {
  return saveFileGeneric(binaryContent, fileHandle, {
    defaultFilename,
    filters,
  });
}

/**
 * Save plain text
 */
export async function saveAsText(
  textContent: string,
  fileHandle?: FileHandle,
  defaultFilename: string = 'export.txt',
): Promise<FileHandle | null> {
  return saveFileGeneric(textContent, fileHandle, {
    defaultFilename,
    filters: [{ name: 'Text', extensions: ['txt'] }],
  });
}
